import { Link, matchPath, useLocation } from "react-router-dom";
import {
  Activity,
  AppWindow,
  Boxes,
  Brain,
  ClipboardList,
  Flame,
  Flag,
  FolderKanban,
  FolderTree,
  Home,
  Layers,
  LayoutDashboard,
  Library,
  Map,
  Rocket,
  ShieldCheck,
  Sparkles,
  Ticket as TicketIcon,
  Users,
  Video,
  Waves,
  Youtube,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Sidebar icon colours (Erez, 2026-07-25). The icons used to inherit the link's
// `text-muted-foreground` grey, so every row looked identical. Colour now carries
// MEANING — it is the domain family, not decoration — so the spine reads as
// groups at a glance rather than as a rainbow. Hexes are the sanctioned
// architecture palette (ArchitectureBlocks `T`), not invented values.
export const NAV_COLOR = {
  hub: "#2A6AAC",       // the command surface
  client: "#B87333",    // amber  — paid client engagements
  growth: "#2A7A4B",    // green  — revenue: business, marketing, content, ventures
  system: "#8E4585",    // plum   — the OS itself: architecture, design, skills, craft
  knowledge: "#3B82C8", // sky    — knowledge, memory and the operating surfaces
} as const;

export type NavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Domain-family colour for the icon. Falls back to the inherited grey. */
  color?: string;
};

export type NavSection = {
  title: string;
  items: NavItem[];
};

// Sidebar spine, grouped into two labelled sections (2026-07-12):
//   • Boards   — the per-domain React-Flow canvases (labels drop the word "Board").
//   • Agent OS — everything else (the operating surfaces + content tools).
// Tickets + Goals live in Paperclip; Projects are the memory-per-project surface.
export const navSections: NavSection[] = [
  {
    // BUSINESS = our own company (Erez, 2026-07-25). Renamed from "Boards".
    // Everything here is MyJarvis/Tailormind itself: how we sell, how we market,
    // and the system we run on. Client work lives one section down.
    title: "Business",
    items: [
      { label: "Jarvis", to: "/command", icon: LayoutDashboard, color: NAV_COLOR.hub },
      { label: "Marketing", to: "/marketing-board", icon: AppWindow, color: NAV_COLOR.growth },
      { label: "Marketing Research", to: "/marketing-research", icon: AppWindow, color: NAV_COLOR.growth },
      { label: "R&D", to: "/board", icon: LayoutDashboard, color: NAV_COLOR.system },
      { label: "Design System", to: "/design-system-board", icon: Layers, color: NAV_COLOR.system },
      { label: "Open Source", to: "/open-source-board", icon: Boxes, color: NAV_COLOR.system },
    ],
  },
  {
    // PROJECTS = the engagements and ventures we run FOR someone — clients first
    // (newest at the top), then our own ventures, then the knowledge surface.
    title: "Projects",
    items: [
      { label: "Liron", to: "/liron-board", icon: Rocket, color: NAV_COLOR.client },
      { label: "Outform", to: "/outform-board", icon: ClipboardList, color: NAV_COLOR.client },
      { label: "Everything 420", to: "/everything-420-board", icon: Map, color: NAV_COLOR.client },
      { label: "Falcon", to: "/falcon-board", icon: Map, color: NAV_COLOR.client },
      { label: "Air Duct", to: "/air-duct-board", icon: Map, color: NAV_COLOR.client },
      { label: "MAX Security", to: "/max-security-board", icon: ShieldCheck, color: NAV_COLOR.client },
      { label: "El HaDegel", to: "/elhadegel-board", icon: Flag, color: NAV_COLOR.client },
      { label: "Elad Engineering", to: "/elad-engineering-board", icon: Waves, color: NAV_COLOR.client },
      { label: "Boiling Offers", to: "/boiling-offers-board", icon: Flame, color: NAV_COLOR.client },
      { label: "YouTube", to: "/youtube-board", icon: Youtube, color: NAV_COLOR.growth },
      { label: "Startup", to: "/startup-board", icon: Rocket, color: NAV_COLOR.growth },
      { label: "Knowledge", to: "/knowledge-board", icon: FolderTree, color: NAV_COLOR.knowledge },
    ],
  },
  {
    title: "Agent OS",
    items: [
      { label: "Home", to: "/home", icon: Home, color: NAV_COLOR.knowledge },
      { label: "Projects", to: "/projects-list", icon: FolderKanban, color: NAV_COLOR.knowledge },
      // Restored 2026-08-11 — the action-item board across every project.
      // Sits next to Projects because it is the same data one level down:
      // a project is the object, a ticket is the work against it.
      { label: "Tickets", to: "/tickets", icon: TicketIcon, color: NAV_COLOR.knowledge },
      { label: "Agents", to: "/agents", icon: Users, color: NAV_COLOR.knowledge },
      { label: "Skills", to: "/skills", icon: Sparkles, color: NAV_COLOR.system },
      { label: "Memory", to: "/memory", icon: Brain, color: NAV_COLOR.knowledge },
      { label: "Meetings", to: "/meetings", icon: Video, color: NAV_COLOR.knowledge },
      { label: "Flame King Manager", to: "/flameking-manager", icon: Activity, color: NAV_COLOR.client },
      { label: "Knowledge Feed", to: "/knowledge-feed", icon: Youtube, color: NAV_COLOR.knowledge },
      { label: "Knowledge Base", to: "/knowledge-base", icon: Library, color: NAV_COLOR.knowledge },
      { label: "Marketing Research", to: "/content-swipe-file", icon: Layers, color: NAV_COLOR.growth },
      { label: "Content Plan", to: "/content-plan", icon: ClipboardList, color: NAV_COLOR.growth },
      { label: "Content Map", to: "/content-map", icon: Map, color: NAV_COLOR.growth },
      { label: "Avatars Lab", to: "/avatars", icon: Sparkles, color: NAV_COLOR.system },
      { label: "3D Showcase", to: "/threejs-showcase", icon: Boxes, color: NAV_COLOR.system },
    ],
  },
];

// Flat list preserved for preview-mode filtering + the mobile nav.
export const navItems: NavItem[] = navSections.flatMap((section) => section.items);

export function NavLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const active =
    item.to === "/"
      ? location.pathname === "/"
      : !!matchPath(item.to + "/*", location.pathname);

  return (
    <Link
      to={item.to}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
        active
          ? "bg-accent text-foreground font-medium"
          : "text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      {/* Inline colour beats the link's inherited `text-muted-foreground`, and it
          stays on when the row is active — the colour is the item's identity, so
          it must not change as you navigate. */}
      <item.icon className="h-[18px] w-[18px] shrink-0" style={{ color: item.color }} />
      <span>{item.label}</span>
    </Link>
  );
}
